import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  Outlet,
  useRouter,
} from '@tanstack/react-router';
import appCss from '../styles.css?url';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { ThemeProvider } from '@upcaret/ui/theme-provider';
import { Toaster } from '@upcaret/ui/sonner';
import { AppProviders } from '../app/providers/app-providers';
import {
  UPCARET_BUSINESS_PHONE_E164,
  UPCARET_BUSINESS_PHONE_VISIBLE,
} from '../app/config/site-contact';

import { getPublicEnv } from '@upcaret/env-utils';
import NotFoundPage from '../app/pages/not-found/NotFoundPage';
import { buildNotFoundHead } from '../utils/seo';

interface RouterContext {
  queryClient: QueryClient;
}

export const PUBLIC_APP_PATHS = [
  // /app is a guarded SPA; auth screens live under /auth/*
];

// CustomGPT initialization component (commented out - to re-enable: uncomment this function,
// uncomment the import for useEffect at the top, uncomment the script tag in head(),
// and uncomment the <CustomGPTInit /> component usage below)
// function CustomGPTInit() {
//   useEffect(() => {
//     // Wait for CustomGPT script to load
//     const initCustomGPT = () => {
//       if (typeof (window as any).CustomGPT !== 'undefined') {
//         try {
//           (window as any).CustomGPT.init({
//             p_id: '87139',
//             p_key: '985a905c72f350647943ad7f1229327f',
//           });
//         } catch (error) {
//           console.error('CustomGPT initialization failed:', error);
//         }
//       }
//     };
//
//     // Check if already loaded
//     if (typeof (window as any).CustomGPT !== 'undefined') {
//       initCustomGPT();
//     } else {
//       // Wait for script to load
//       const checkInterval = setInterval(() => {
//         if (typeof (window as any).CustomGPT !== 'undefined') {
//           clearInterval(checkInterval);
//           initCustomGPT();
//         }
//       }, 100);
//
//       // Clean up interval after 10 seconds
//       const timeout = setTimeout(() => {
//         clearInterval(checkInterval);
//       }, 10000);
//
//       return () => {
//         clearInterval(checkInterval);
//         clearTimeout(timeout);
//       };
//     }
//   }, []);
//
//   return null;
// }

const SITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'LocalBusiness',
      '@id': 'https://upcaret.com/#organization',
      name: 'upCaret',
      url: 'https://upcaret.com',
      logo: 'https://upcaret.com/og-image.png',
      image: 'https://upcaret.com/og-image.png',
      description:
        'upCaret is an AI agency building custom AI agents, integrations, and automation for businesses in Omaha and beyond.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '4601 Catalyst Ct, Desk 2083',
        addressLocality: 'Omaha',
        addressRegion: 'NE',
        postalCode: '68106',
        addressCountry: 'US',
      },
      ...(UPCARET_BUSINESS_PHONE_VISIBLE
        ? { telephone: UPCARET_BUSINESS_PHONE_E164 }
        : {}),
      sameAs: [
        'https://github.com/chmoder',
        'https://www.linkedin.com/company/upcaret',
        'https://www.facebook.com/upcaret',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': 'https://upcaret.com/#website',
      url: 'https://upcaret.com',
      name: 'upCaret',
      publisher: { '@id': 'https://upcaret.com/#organization' },
    },
  ],
};

export const Route = createRootRouteWithContext<RouterContext>()({
  head: ({ match }) => {
    const isNotFound = match.status === 'notFound';

    return {
      ...(isNotFound ? buildNotFoundHead() : {}),
      links: [{ rel: 'stylesheet', href: appCss }],
      // scripts: [
      //   {
      //     src: 'https://cdn.customgpt.ai/js/chat.js',
      //     defer: true,
      //   },
      // ],
    };
  },
  notFoundComponent: NotFoundPage,
  component: () => {
    const { options } = useRouter();
    const queryClient = options.context.queryClient;

    // Serialize runtime public env into the SSR'd HTML so the client has it
    // before any app module evaluates. In production the Cloudflare Worker
    // also injects this (defense in depth); in dev this is the ONLY injection
    // point, since TanStack Start's SSR bypasses Vite's transformIndexHtml.
    // Escape "<" to prevent breaking out of the inline <script>.
    const publicEnvJson = JSON.stringify(getPublicEnv()).replace(
      /</g,
      '\\u003c',
    );

    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="theme-color" content="#2563eb" />
          <base href="/" />
          <script
            type="application/json"
            id="upcaret-public-env"
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: publicEnvJson }}
          />
          <script src="/env-bootstrap.js" suppressHydrationWarning />
          <HeadContent />
          <style
            dangerouslySetInnerHTML={{
              __html:
                'body{margin:0;background:radial-gradient(circle at 10% 0%,rgb(37 99 235/.05),transparent 42%),radial-gradient(circle at 92% 12%,rgb(23 23 23/.03),transparent 38%),#fafafa}',
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(SITE_SCHEMA),
            }}
          />
        </head>
        <body>
          <ThemeProvider defaultTheme="light" storageKey="upcaret-theme">
            <QueryClientProvider client={queryClient}>
              <AppProviders>
                {/* <CustomGPTInit /> */}
                <Outlet />
                <Toaster />
              </AppProviders>
            </QueryClientProvider>
          </ThemeProvider>
          <Scripts />
        </body>
      </html>
    );
  },
});
